/**
 * Push Subscription Service
 *
 * Shared module for Web Push notification subscription.
 * Used by both first_visit_prompt_controller and push_subscription_controller.
 */

const SERVICE_WORKER_PATH = "/service-worker.js"
const VAPID_KEY_ENDPOINT = "/api/push_subscriptions/vapid_public_key"
const SUBSCRIPTION_ENDPOINT = "/api/push_subscriptions"

// ─────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────

/**
 * Subscribe to push notifications
 * @returns {Promise<boolean>} Success status
 */
export async function subscribeToPush() {
  try {
    const registration = await registerServiceWorker()
    if (!registration) throw new Error("Service Worker registration failed")

    const vapidKey = await fetchVapidPublicKey()
    if (!vapidKey) throw new Error("VAPID key fetch failed")

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    })

    return await saveSubscription(subscription)
  } catch (error) {
    console.error("[PushService] Subscribe failed:", error)
    return false
  }
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<boolean>} Success status
 */
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return true

    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return true

    const success = await subscription.unsubscribe()
    if (success) {
      await deleteSubscription(subscription.endpoint)
    }
    return success
  } catch (error) {
    console.error("[PushService] Unsubscribe failed:", error)
    return false
  }
}

/**
 * Check if user is currently subscribed
 * @returns {Promise<boolean>}
 */
export async function isSubscribed() {
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return false

    const subscription = await registration.pushManager.getSubscription()
    return subscription !== null
  } catch {
    return false
  }
}

/**
 * Check if push notifications are supported
 * @returns {boolean}
 */
export function isSupported() {
  return ("serviceWorker" in navigator) &&
         ("PushManager" in window) &&
         ("Notification" in window)
}

// ─────────────────────────────────────────────────────────────
// Internal Helpers
// ─────────────────────────────────────────────────────────────

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null

  try {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
    await navigator.serviceWorker.ready
    return registration
  } catch (error) {
    console.error("[PushService] SW registration failed:", error)
    return null
  }
}

async function fetchVapidPublicKey() {
  try {
    const response = await fetch(VAPID_KEY_ENDPOINT)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const data = await response.json()
    return data.public_key
  } catch (error) {
    console.error("[PushService] VAPID key fetch failed:", error)
    return null
  }
}

async function saveSubscription(subscription) {
  try {
    const response = await fetch(SUBSCRIPTION_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: JSON.stringify({ subscription: subscription.toJSON() })
    })
    return response.ok
  } catch (error) {
    console.error("[PushService] Save failed:", error)
    return false
  }
}

async function deleteSubscription(endpoint) {
  try {
    await fetch(SUBSCRIPTION_ENDPOINT, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: JSON.stringify({ endpoint })
    })
  } catch (error) {
    console.error("[PushService] Delete failed:", error)
  }
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = window.atob(base64)
  return Uint8Array.from(rawData, char => char.charCodeAt(0))
}
